import * as React from "react";

import { extendSelection } from "@/shared/array";
import {
  Checkbox,
  Radio,
  RadioGroup,
  type TextInputSize,
} from "@/shared/foundation/primitives";

import { type BooleanComparator, isValueless } from "../FilterSchema";
import { listMemberLabels } from "../operators";

import { ListMember } from "./ListMember";
import styles from "./valueInputs.module.css";

interface BooleanValueInputProps {
  comparator: BooleanComparator;
  value: boolean[];
  onChange: (next: boolean[]) => void;
  /** Field size; a radio has no `lg`, so it reads as `md`. @default "md" */
  size?: TextInputSize;
  /**
   * Whether the field's value can be absent, which is what makes "Not set" a
   * member worth offering under `ANY_OF`. @default false
   */
  nullable?: boolean;
  /** Whether "Not set" is a member of an `ANY_OF` selection. @default false */
  includeNull?: boolean;
  /**
   * Called when the author ticks or unticks "Not set". Without it, "Not set"
   * is never offered.
   */
  onIncludeNullChange?: (includeNull: boolean) => void;
}

// The checklist's box values. "Not set" is a flag of the filter rather than a
// value, but a checkbox group names its boxes by string, so it gets one too.
const TRUE = "true";
const FALSE = "false";
const NOT_SET = "not-set";

const MEMBER_LABELS = listMemberLabels("boolean");

/**
 * Value input for boolean-typed filters.
 *
 * Operator-aware:
 *   - IS_NULL / IS_NOT_NULL render no input; validity short-circuits to true.
 *   - EQUALS renders a True / False radio pair. The user must pick one to
 *     enable Add filter (no implicit default — boolean fields can be
 *     meaningfully nullable, and presupposing a default would silently
 *     filter for one truth value).
 *   - ANY_OF renders a True / False checklist, plus "Not set" on a nullable
 *     field, for "any of these" across the three states a nullable boolean
 *     can be in. Each box reads after the comparator it stands for ("equals
 *     True"), and "Not set" is that pill alone ("is not set"). Shift+click
 *     (or Shift+Space) on True or False, after
 *     pressing the other, is a two-box range: both take the state the
 *     pressed box toggles to. "Not set" stays out of ranges.
 */
export function BooleanValueInput({
  comparator,
  value,
  onChange,
  size = "md",
  nullable = false,
  includeNull = false,
  onIncludeNullChange,
}: BooleanValueInputProps) {
  // The ANY_OF checklist's range anchor: the True or False box last pressed.
  const anchor = React.useRef<string | undefined>(undefined);

  if (isValueless(comparator)) {
    return null;
  }

  const controlSize = size === "sm" ? "sm" : "md";

  if (comparator === "ANY_OF") {
    const offersNotSet = nullable && onIncludeNullChange !== undefined;
    const ticked = [
      ...(value.includes(true) ? [TRUE] : []),
      ...(value.includes(false) ? [FALSE] : []),
      ...(includeNull ? [NOT_SET] : []),
    ];
    return (
      // A shift-held mousedown extends the page's text selection by default,
      // which would highlight the labels a shift+click passes over.
      <div
        className={styles.eventScope}
        onMouseDown={(event) => {
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
      >
        <Checkbox.Group
          aria-label="Values"
          size={controlSize}
          value={ticked}
          onValueChange={(next, details) => {
            // "Not set" is a flag of the filter rather than one of its values,
            // so it neither ends nor anchors a range.
            const pressed = [TRUE, FALSE].find(
              (box) => next.includes(box) !== ticked.includes(box),
            );
            let values = [
              ...(next.includes(TRUE) ? [true] : []),
              ...(next.includes(FALSE) ? [false] : []),
            ];
            if (pressed !== undefined) {
              if (isShiftHeld(details.event)) {
                const extended = extendSelection({
                  selected: value,
                  visible: [true, false],
                  anchor: anchor.current,
                  target: pressed === TRUE,
                  keyOf: String,
                });
                values = [true, false].filter((item) =>
                  extended.includes(item),
                );
              }
              anchor.current = pressed;
            }
            if (
              values.length !== value.length ||
              values.some((item, index) => item !== value[index])
            ) {
              onChange(values);
            }
            const nextIncludeNull = next.includes(NOT_SET);
            if (nextIncludeNull !== includeNull) {
              onIncludeNullChange?.(nextIncludeNull);
            }
          }}
        >
          <Checkbox
            value={TRUE}
            label={
              <ListMember comparator={MEMBER_LABELS.value}>True</ListMember>
            }
          />
          <Checkbox
            value={FALSE}
            label={
              <ListMember comparator={MEMBER_LABELS.value}>False</ListMember>
            }
          />
          {(offersNotSet || includeNull) && (
            <Checkbox
              value={NOT_SET}
              label={<ListMember comparator={MEMBER_LABELS.notSet} />}
            />
          )}
        </Checkbox.Group>
      </div>
    );
  }

  return (
    <RadioGroup<boolean>
      aria-label="Value"
      size={controlSize}
      value={value.length === 1 ? value[0] : null}
      onValueChange={(next) => {
        onChange([next]);
      }}
    >
      <Radio value label="True" />
      <Radio value={false} label="False" />
    </RadioGroup>
  );
}

/** Whether Shift was held for the click or key press that ticked a box. */
function isShiftHeld(event: Event): boolean {
  return (
    (event instanceof MouseEvent || event instanceof KeyboardEvent) &&
    event.shiftKey
  );
}
